<template>
  <div class="rrw-report pa-4" :class="{ 'rrw-report--dark': isDark }">

    <!-- =============================================================== -->
    <!-- filter and search/compare buttons component -->
    <RequestsReceivedWebReportFilter :mode="mode" :loading="loading" :compare-loading="compareLoading"
      @search="fetchReport('main', $event)" @compare="fetchReport('compare', $event)" />
    <!-- =============================================================== -->


    <!-- =============================================================== -->
    <!-- dates component -->
    <v-row v-if="reportMeta.startDate" class="rrw-report__dates mb-2">
      <v-col cols="12" md="6">
        <span class="rrw-report__date-label">التاريخ من:</span>
        <strong>{{ reportMeta.startDate }}</strong>
      </v-col>
      <v-col cols="12" md="6">
        <span class="rrw-report__date-label">التاريخ إلى:</span>
        <strong>{{ reportMeta.endDate }}</strong>
      </v-col>
    </v-row>

    <!-- =============================================================== -->
    <!-- summary cards component (received, transferred, total) -->
    <v-row v-if="hasData" class="mb-4">
      <v-col cols="12" sm="4" v-for="card in summaryCards" :key="card.key">
        <v-card class="rrw-report__summary-card pa-4" elevation="1">
          <div class="rrw-report__summary-dot" :style="{ background: card.color }"></div>
          <div class="rrw-report__summary-value">{{ card.value }}</div>
          <div class="rrw-report__summary-label">{{ card.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- =============================================================== -->
    <!-- chart component -->
    <v-card v-if="hasData" class="rrw-report__chart-card pa-3 mb-4" elevation="1">
      <div ref="mainChart" class="rrw-report__chart"></div>
    </v-card>

    <!-- =============================================================== -->
    <!-- compare result (counts + chart) -->
    <template v-if="allowCompare && compareVisible && hasCompareData">
      <div class="rrw-report__compare-title mb-2">نتيجة المقارنة</div>

      <v-row v-if="compareMeta.startDate" class="rrw-report__dates mb-2">
        <v-col cols="12" md="6">
          <span class="rrw-report__date-label">التاريخ من:</span>
          <strong>{{ compareMeta.startDate }}</strong>
        </v-col>
        <v-col cols="12" md="6">
          <span class="rrw-report__date-label">التاريخ إلى:</span>
          <strong>{{ compareMeta.endDate }}</strong>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12" sm="4" v-for="card in compareSummaryCards" :key="card.key">
          <v-card class="rrw-report__summary-card pa-4" elevation="1">
            <div class="rrw-report__summary-dot" :style="{ background: card.color }"></div>
            <div class="rrw-report__summary-value">{{ card.value }}</div>
            <div class="rrw-report__summary-label">{{ card.label }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="rrw-report__chart-card pa-3 mb-4" elevation="1">
        <div ref="compareChart" class="rrw-report__chart"></div>
      </v-card>
    </template>

    <!-- =============================================================== -->
    <!-- table component -->
    <v-card v-if="hasData" class="rrw-report__table-card" elevation="1">
      <v-card-text class="pb-0">
        <v-text-field v-model="tableAgentSearch" label="بحث عن استشاري" dense outlined hide-details clearable
          prepend-inner-icon="mdi-magnify" class="rrw-report__table-search" @input="onTableAgentSearchInput"
          @click:clear="onTableAgentSearchClear" />
      </v-card-text>
      <v-data-table :headers="tableHeaders" :items="tableData" :loading="tableLoading" loading-text="جاري التحميل..."
        no-data-text="لا يوجد نتائج" :server-items-length="tablePagination.total"
        :items-per-page="tablePagination.perPage" :page.sync="tablePagination.page"
        :footer-props="{ 'items-per-page-options': [tablePagination.perPage] }" item-key="id"
        class="rrw-report__table elevation-0" @update:page="fetchTablePage" />
    </v-card>

    <!-- =============================================================== -->
    <!-- empty state component -->
    <v-card v-else-if="!loading && loaded" class="pa-6 text-center rrw-report__empty" flat>
      لا يوجد نتائج للفترة المحددة
    </v-card>
    <!-- =============================================================== -->
    <!-- loader component -->
    <div v-if="loading && !loaded" class="rrw-report__loader">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>
    <!-- =============================================================== -->
  </div>
</template>

<script>
  import Axios from "axios";
  import Highcharts from "highcharts";
  import RequestsReceivedWebReportFilter from "./RequestsReceivedWebReportFilter.vue";

  import {
    buildRequestsReceivedWebReportChartOptions,
    extractReceivedWebReportChartSeries,
  } from "@/helper/requestsReceivedWebReportChart.js";

  import {
    REQUESTS_RECEIVED_WEB_REPORT_MODES,
    applyRequestsReceivedWebTablePagination,
    buildRequestsReceivedWebReportFormBody,
    getRequestsReceivedWebReportUrl,
    todayYmd,
  } from "@/helper/requestsReceivedWebReportApi.js";

  const TABLE_PER_PAGE = 25;

  export default {
    name: "RequestsReceivedWebReportPage",
    components: { RequestsReceivedWebReportFilter },
    props: {
      mode: {
        type: String,
        default: REQUESTS_RECEIVED_WEB_REPORT_MODES.ADMIN,
        validator: (value) =>
          Object.values(REQUESTS_RECEIVED_WEB_REPORT_MODES).includes(value),
      },
    },
    data() {
      return {
        loading:        false,  
        compareLoading: false,
        tableLoading:   false,
        loaded:         false, 
        compareVisible: false,
        hasCompareData: false,
        filters: {
          startDate: todayYmd(),
          endDate: todayYmd(),
          statusUser: 1,
          managerId: [],
          adviserId: [],
        },
        reportMeta: {},
        compareMeta: {},
        totalAgents: 0,
        tableData: [],
        tableAgentSearch: "",
        tableSearchTimer: null,
        tablePageSyncing: false,
        tablePagination: {
          page: 1,
          perPage: TABLE_PER_PAGE,
          total: 0,
          lastPage: 1,
        },
        summary: { received: 0, transferred: 0, total: 0 },
        compareSummary: { received: 0, transferred: 0, total: 0 },
        chartSeries: { categories: [], receivedSeries: [], transferredSeries: [] },
        compareChartSeries: { categories: [], receivedSeries: [], transferredSeries: [] },
        mainChartInstance: null,
        compareChartInstance: null,
        tableHeaders: [
          { text: "الإستشاري", value: "agent_name", align: "center" },
          { text: "الطلبات المستلمة", value: "received_count", align: "center" },
          { text: "الطلبات المحولة", value: "transferred_count", align: "center" },
          { text: "المجموع", value: "total", align: "center" },
        ],
      };
    },
    computed: {
      isDark() {
        return this.$store.getters.isDark;
      },
      allowCompare() {
        return this.mode !== REQUESTS_RECEIVED_WEB_REPORT_MODES.SALES_MANAGER;
      },
      hasData() {
        return this.totalAgents > 0 || this.chartSeries.categories.length > 0;
      },
      summaryCards() {
        return this.buildSummaryCards(this.summary);
      },
      compareSummaryCards() {
        return this.buildSummaryCards(this.compareSummary, "compare");
      },
    },
    watch: {
      isDark() {
        this.renderCharts();
      },
    },
    beforeDestroy() {
      this.destroyCharts();
      if (this.tableSearchTimer) {
        clearTimeout(this.tableSearchTimer);
      }
    },
    methods: {
      //===============================================================
      // summary cards for a report result
      buildSummaryCards(summary, keyPrefix = "") {
        return [
          {
            key: `${keyPrefix}received`,
            label: "الطلبات المستلمة",
            value: summary.received,
            color: "#22C55E",
          },
          {
            key: `${keyPrefix}transferred`,
            label: "الطلبات المحولة",
            value: summary.transferred,
            color: "#3B82F6",
          },
          {
            key: `${keyPrefix}total`,
            label: "مجموع الطلبات",
            value: summary.total,
            color: "#EF4444",
          },
        ];
      },
      //===============================================================
      // build form body
      buildFormBody(includeTableParams = true) {
        return buildRequestsReceivedWebReportFormBody(this.filters, {
          includeTableParams,
          page: this.tablePagination.page,
          perPage: this.tablePagination.perPage,
          agentSearch: this.tableAgentSearch,
        });
      },
      //===============================================================
      // apply table payload
      applyTablePayload(payload) {
        this.tablePageSyncing = true;
        const { tableData, tablePagination } = applyRequestsReceivedWebTablePagination(
          payload,
          TABLE_PER_PAGE
        );
        this.tableData = tableData;
        this.tablePagination = tablePagination;
        this.$nextTick(() => {
          this.tablePageSyncing = false;
        });
      },
      //===============================================================
      // on table agent search input
      onTableAgentSearchInput() {
        if (this.tableSearchTimer) {
          clearTimeout(this.tableSearchTimer);
        }
        this.tableSearchTimer = setTimeout(() => {
          this.tablePagination.page = 1;
          this.fetchTablePage();
        }, 400);
      },
      //===============================================================
      // on table agent search clear
      onTableAgentSearchClear() {
        this.tableAgentSearch = "";
        this.tablePagination.page = 1;
        this.fetchTablePage();
      },
      //===============================================================
      // fetch table data
      fetchTablePage(page) {
        if (!this.loaded || this.loading || this.tablePageSyncing) return;
        if (typeof page === "number") {
          this.tablePagination.page = page;
        }

        this.tableLoading = true;
        const url = `${this.$store.state.url}${getRequestsReceivedWebReportUrl("main")}`;

        Axios.post(url, this.buildFormBody(true), { headers: this.request_headers() })
          .then((response) => {
            this.applyTablePayload(response.data.payload || {});
          })
          .catch((error) => {
            this.showPopup("error", error.message);
          })
          .finally(() => {
            this.tableLoading = false;
          });
      },
      //===============================================================
      // fetch report data
      fetchReport(mode = "main", filterPayload = null) {
        if (filterPayload) { // if filter payload is provided, update filters
          this.filters = filterPayload;
        }
        const isCompare = mode === "compare";
        if (isCompare && !this.allowCompare) {
          return;
        }
        if (isCompare) {
          this.compareLoading = true;
        } else {
          this.loading = true;
          this.tablePagination.page = 1;
          this.tableAgentSearch = "";
        }

        const url = `${this.$store.state.url}${getRequestsReceivedWebReportUrl(mode)}`;

        Axios.post(url, this.buildFormBody(!isCompare), { headers: this.request_headers() })
          .then((response) => {
            const payload = response.data.payload || {};
            const series = extractReceivedWebReportChartSeries(payload);

            if (isCompare) {
              this.compareMeta = {
                startDate: payload.startDate,
                endDate: payload.endDate,
              };
              this.compareChartSeries = series;
              this.compareSummary = series.totals;
              this.compareVisible = true;
              this.hasCompareData = series.categories.length > 0;
              this.$nextTick(() => this.renderCompareChart());
              return;
            }

            this.reportMeta = {
              startDate: payload.startDate,
              endDate: payload.endDate,
            };
            this.totalAgents = payload.total_agents || series.categories.length;
            this.applyTablePayload(payload);
            this.chartSeries = series;
            this.summary = series.totals;
            this.loaded = true;
            this.$nextTick(() => this.renderMainChart());
          })
          .catch((error) => {
            this.showPopup("error", error.message);
          })
          .finally(() => {
            if (isCompare) {
              this.compareLoading = false;
            } else {
              this.loading = false;
            }
          });
      },
      //===============================================================
      // destroy charts
      destroyCharts() {
        if (this.mainChartInstance) {
          this.mainChartInstance.destroy();
          this.mainChartInstance = null;
        }
        if (this.compareChartInstance) {
          this.compareChartInstance.destroy();
          this.compareChartInstance = null;
        }
      },
      //===============================================================
      // render charts
      renderCharts() {
        this.renderMainChart();
        if (this.compareVisible) {
          this.renderCompareChart();
        }
      },
      //===============================================================
      // render main chart
      renderMainChart() {
        if (!this.$refs.mainChart || !this.chartSeries.categories.length) return;
        if (this.mainChartInstance) {
          this.mainChartInstance.destroy();
        }
        this.mainChartInstance = Highcharts.chart(
          this.$refs.mainChart,
          buildRequestsReceivedWebReportChartOptions({
            categories: this.chartSeries.categories,
            receivedSeries: this.chartSeries.receivedSeries,
            transferredSeries: this.chartSeries.transferredSeries,
            isDark: this.isDark,
            title: "تقرير استلام الطلبات",
          })
        );
      },
      //===============================================================
      // render compare chart
      renderCompareChart() {
        if (!this.$refs.compareChart || !this.compareChartSeries.categories.length) return;
        if (this.compareChartInstance) {
          this.compareChartInstance.destroy();
        }
        this.compareChartInstance = Highcharts.chart(
          this.$refs.compareChart,
          buildRequestsReceivedWebReportChartOptions({
            categories: this.compareChartSeries.categories,
            receivedSeries: this.compareChartSeries.receivedSeries,
            transferredSeries: this.compareChartSeries.transferredSeries,
            isDark: this.isDark,
            title: "مقارنة تقرير استلام الطلبات",
          })
        );
      },
      //===============================================================
    },
  };
</script>

<style scoped lang="scss">
.rrw-report {
  color: #1f2937;

  &--dark {
    color: #e8eaed;
  }

  &__dates {
    font-size: 15px;
  }

  &__date-label {
    opacity: 0.85;
    margin-left: 6px;
  }

  &__summary-card {
    border-radius: 12px;
    text-align: center;
    min-height: 110px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  &__summary-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
  }

  &__summary-value {
    font-size: 28px;
    font-weight: 800;
    line-height: 1.1;
  }

  &__summary-label {
    font-size: 14px;
    font-weight: 600;
    opacity: 0.9;
  }

  &__table-search {
    max-width: 360px;
    margin-bottom: 8px;
  }

  &__chart-card,
  &__table-card {
    border-radius: 12px;
    overflow: hidden;
  }

  &__chart {
    width: 100%;
    min-height: 480px;
  }

  &__compare-title {
    font-size: 16px;
    font-weight: 700;
  }

  &__empty {
    border-radius: 12px;
    opacity: 0.85;
  }

  &__loader {
    display: flex;
    justify-content: center;
    padding: 48px 0;
  }
}

.rrw-report--dark {

  .rrw-report__summary-card,
  .rrw-report__chart-card,
  .rrw-report__table-card,
  .rrw-report__empty {
    background: #1e1e1e !important;
    border: 1px solid #3a3a3a;
  }
}

.rrw-report--dark ::v-deep .v-data-table,
.rrw-report--dark ::v-deep .v-data-table th,
.rrw-report--dark ::v-deep .v-data-table td {
  background: #1e1e1e !important;
  color: #e8eaed !important;
}
</style>
